import { apiClient } from "@/lib/apiClient";
import i18n from "@/utils/i18n";
import {
  InternStatus,
  type Intern,
  type InternDetail,
  type InternFilters,
  type InternType,
  type ApplicationSource,
  type InternTypeV2,
  type InternSubCategory,
  type InternLocation,
  combineInternTypeLegacy,
} from "../types/intern-management.types";

const ENDPOINT = "/api/admin/interns";

/** API list item – GET /api/admin/interns list */
interface InternListItemApi {
  id: string;
  userId?: string;
  userID?: string;
  fullName?: string;
  identityNumber?: string;
  tc?: string;
  nationalId?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  internshipType?: number;
  internType?: number;
  applicationSource?: number;
  application_source?: number;
  departmentID?: string | null;
  departmentId?: string | null;
  department?: { id: string; name: string } | null;
  company?: { id: string; name: string } | null;
  departmentName?: string;
  unitOrBusiness?: string;
  unit?: string;
  progressPercent?: number | null;
  educationProgress?: number;
  education_progress?: number;
  progress?: number;
  status?: number;
  createdAt?: string;
  created_at?: string;
  /** Sertifika PDF'i hazır mı (BullMQ işi tamamlandıysa true) */
  hasCertificate?: boolean;
}

/** GET /api/admin/interns/:id – full detail response */
interface InternDetailApi extends InternListItemApi {
  phone?: string;
  phoneNumber?: string;
  email?: string;
  iban?: string;
  address?: string;
  school?: string;
  schoolDepartment?: string;
  grade?: string;
  schoolNumber?: string | null;
  startDate?: string;
  endDate?: string;
  supervisorID?: string | null;
  supervisorName?: string | null;
  supervisorContactInfo?: string | null;
  supervisor?: { id?: string; firstName?: string; lastName?: string };
  documentStatus?: number;

  /** BE: numeric (0=ZORUNLU_YAZ_STAJI, 1=IS_YERI_EGITIMI, 2=BECERI_EGITIMI) */
  subCategory?: number | null;
  /** BE: numeric (1=MERKEZ/CENTRAL, 2=TASRA/BRANCH) — department.type'tan türetilir */
  internshipLocation?: number | null;
  /** Lise özel alanları (BE field adları) */
  parentName?: string | null;
  parentContactInfo?: string | null;
  coordinatorTeacherName?: string | null;
  coordinatorTeacherContact?: string | null;
}

/** List response: data or items, total or count */
interface InternListResponseApi {
  data?: InternListItemApi[];
  items?: InternListItemApi[];
  total?: number;
  count?: number;
}

// API: internshipType 0 = Üniversite (Yaz Stajı / İş Yeri Eğitimi), 1 = Lise (Beceri Eğitimi / Lise Yaz)
const INTERN_TYPE_FROM_API: Record<number, InternType> = {
  0: "universiteZorunlu",
  1: "liseBeceri",
};

const INTERN_TYPE_TO_API: Record<InternType, number> = {
  universiteZorunlu: 0,
  liseBeceri: 1,
};

const APPLICATION_SOURCE_FROM_API: Record<number, ApplicationSource> = {
  0: "kariyerKapisi",
  1: "manuel",
};

const APPLICATION_SOURCE_TO_API: Record<ApplicationSource, number> = {
  kariyerKapisi: 0,
  manuel: 1,
};

/** Frontend V2 enum → backend `InternshipType` numeric enum (UNIVERSITY=0, LISE=1). */
const INTERNSHIP_TYPE_V2_TO_API: Record<InternTypeV2, number> = {
  UNIVERSITY: 0,
  HIGH_SCHOOL: 1,
};

/** Frontend → backend `InternSubCategory` numeric enum
 *  (ZORUNLU_YAZ_STAJI=0, IS_YERI_EGITIMI=1, BECERI_EGITIMI=2). */
const SUB_CATEGORY_TO_API: Record<InternSubCategory, number> = {
  MANDATORY_SUMMER: 0,
  WORKPLACE_TRAINING: 1,
  SKILL_TRAINING: 2,
};

/** Frontend `InternLocation` → backend `DepartmentType` (CENTRAL=1, BRANCH=2).
 *  Backend query alanı `internshipLocation`. */
const INTERN_LOCATION_TO_API: Record<InternLocation, number> = {
  CENTER: 1,
  PROVINCIAL: 2,
};

/** BE numeric → FE union (read tarafı). */
const INTERNSHIP_TYPE_V2_FROM_API: Record<number, InternTypeV2> = {
  0: "UNIVERSITY",
  1: "HIGH_SCHOOL",
};

const SUB_CATEGORY_FROM_API: Record<number, InternSubCategory> = {
  0: "MANDATORY_SUMMER",
  1: "WORKPLACE_TRAINING",
  2: "SKILL_TRAINING",
};

const INTERN_LOCATION_FROM_API: Record<number, InternLocation> = {
  1: "CENTER",
  2: "PROVINCIAL",
};

const isInternStatus = (value: unknown): value is InternStatus =>
  value === InternStatus.AKTIF || value === InternStatus.TAMAMLANDI;

function mapInternFromApi(item: InternListItemApi): Intern {
  const name =
    item.fullName ??
    item.name ??
    ([item.firstName, item.lastName].filter(Boolean).join(" ") || "—");
  const tc = item.identityNumber ?? item.tc ?? item.nationalId ?? "—";
  const internshipTypeNum = item.internshipType ?? item.internType ?? 0;
  const applicationSourceNum =
    item.applicationSource ?? item.application_source ?? 0;
  const statusNum = item.status ?? InternStatus.AKTIF;
  const progress =
    item.progressPercent ??
    item.educationProgress ??
    item.education_progress ??
    item.progress ??
    0;
  const deptName = item.department?.name;
  const compName = item.company?.name;
  const unitOrBusiness =
    deptName && compName
      ? `${deptName} / ${compName}`
      : (deptName ??
        compName ??
        item.unitOrBusiness ??
        item.departmentName ??
        item.unit ??
        "—");
  const createdAt = item.createdAt ?? item.created_at ?? "";

  // BE numeric enum → FE union string
  const detailItem = item as Partial<InternDetailApi>;
  const internTypeV2 = INTERNSHIP_TYPE_V2_FROM_API[internshipTypeNum];
  const subCategory =
    typeof detailItem.subCategory === "number"
      ? SUB_CATEGORY_FROM_API[detailItem.subCategory]
      : undefined;
  const location =
    typeof detailItem.internshipLocation === "number"
      ? INTERN_LOCATION_FROM_API[detailItem.internshipLocation]
      : undefined;

  // Eski combined tipi: BE'den geliyorsa map'le, yoksa yeniden birleştir
  const combinedLegacy =
    INTERN_TYPE_FROM_API[internshipTypeNum] ??
    (internshipTypeNum === 1 ? "liseBeceri" : "universiteZorunlu");
  const legacyType =
    internTypeV2 || subCategory
      ? combineInternTypeLegacy(internTypeV2, subCategory)
      : combinedLegacy;

  // İsim / soyad ayrımı (liste kolonları için)
  const firstName = item.firstName ?? undefined;
  const lastName = item.lastName ?? undefined;

  return {
    id: item.id,
    userId: item.userId ?? item.userID,
    tc,
    name,
    firstName,
    lastName,
    internType: legacyType,
    applicationSource:
      APPLICATION_SOURCE_FROM_API[applicationSourceNum] ?? "kariyerKapisi",
    unitOrBusiness,
    educationProgress: Math.min(100, Math.max(0, Number(progress))),
    status: isInternStatus(statusNum) ? statusNum : InternStatus.AKTIF,
    createdAt,
    internTypeV2,
    subCategory,
    location,
    startDate: detailItem.startDate ?? undefined,
    endDate: detailItem.endDate ?? undefined,
    school: detailItem.school ?? undefined,
    schoolDepartment: detailItem.schoolDepartment ?? undefined,
    hasCertificate:
      typeof item.hasCertificate === "boolean" ? item.hasCertificate : undefined,
  };
}

function mapInternDetailFromApi(item: InternDetailApi): InternDetail {
  const base = mapInternFromApi(item);
  return {
    ...base,
    phone: item.phone ?? item.phoneNumber ?? undefined,
    email: item.email ?? undefined,
    iban: item.iban ?? undefined,
    address: item.address ?? undefined,
    school: item.school ?? undefined,
    schoolDepartment: item.schoolDepartment ?? undefined,
    grade: item.grade ?? undefined,
    schoolNumber: item.schoolNumber ?? undefined,
    startDate: item.startDate ?? undefined,
    endDate: item.endDate ?? undefined,
    departmentID:
      item.departmentID ??
      item.departmentId ??
      item.department?.id ??
      undefined,
    supervisorID: item.supervisorID ?? undefined,
    supervisorName:
      item.supervisorName ??
      (item.supervisor
        ? [item.supervisor.firstName, item.supervisor.lastName]
            .filter(Boolean)
            .join(" ") || undefined
        : undefined),
    supervisorContact: item.supervisorContactInfo ?? undefined,
    // Lise özel alanları (BE field adlarıyla uyumlu)
    parentName: item.parentName ?? undefined,
    parentContact: item.parentContactInfo ?? undefined,
    coordinatorName: item.coordinatorTeacherName ?? undefined,
    coordinatorContact: item.coordinatorTeacherContact ?? undefined,
  };
}

export {
  INTERN_TYPE_TO_API,
  APPLICATION_SOURCE_TO_API,
  INTERNSHIP_TYPE_V2_TO_API,
  SUB_CATEGORY_TO_API,
  INTERN_LOCATION_TO_API,
};

/** POST /api/admin/interns — backend `CreateInternUserDto` ile birebir uyumlu.
 *  applicationSource BE'de servis tarafından deterministik hesaplanır; payload'da
 *  yer almaz. internTypeV2/location/departmentType DTO'nun whitelist'inde değildir
 *  ve gönderilmez. */
export interface CreateInternPayload {
  phoneNumber: string;
  email?: string;
  firstName: string;
  lastName: string;
  identityNumber: string;
  iban?: string;
  address?: string;
  school: string;
  schoolDepartment: string;
  grade: string;
  schoolNumber?: string;
  startDate: string;
  endDate: string;
  /** Backend numeric enum (UNIVERSITY=0, LISE=1) */
  internshipType: number;
  /** Backend numeric enum (ZORUNLU_YAZ_STAJI=0, IS_YERI_EGITIMI=1, BECERI_EGITIMI=2) */
  subCategory: number;
  departmentID?: string;
  status: number;
  supervisorID?: string;
  supervisorName?: string;
  supervisorContactInfo?: string;
  documentStatus?: number;
  // Lise (high school) zorunlu alanları
  parentName?: string;
  parentContactInfo?: string;
  coordinatorTeacherName?: string;
  coordinatorTeacherContact?: string;
}

export const internManagementApi = {
  /**
   * GET /api/admin/interns - List all interns.
   * Query: page, limit, search, supervisorID, status, departmentID, applicationSource, internshipType, sortBy, sortOrder.
   */
  getAll: async (params?: {
    page?: number;
    pageSize?: number;
    filters?: InternFilters;
    supervisorID?: string;
    departmentID?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }) => {
    const page = params?.page ?? 1;
    const limit = params?.pageSize ?? 10;
    const query: Record<string, string | number | undefined> = {
      page,
      limit,
    };
    if (params?.filters?.search) query.search = params.filters.search;
    if (params?.supervisorID) query.supervisorID = params.supervisorID;
    if (params?.departmentID) query.departmentID = params.departmentID;
    if (params?.sortBy) query.sortBy = params.sortBy;
    if (params?.sortOrder) query.sortOrder = params.sortOrder;

    if (params?.filters?.status != null) {
      query.status = params.filters.status;
    }
    if (params?.filters?.applicationSource != null) {
      query.applicationSource =
        APPLICATION_SOURCE_TO_API[params.filters.applicationSource];
    }
    if (params?.filters?.internTypeV2) {
      query.internshipType =
        INTERNSHIP_TYPE_V2_TO_API[params.filters.internTypeV2];
    } else if (params?.filters?.internType != null) {
      query.internshipType = INTERN_TYPE_TO_API[params.filters.internType];
    }
    if (params?.filters?.subCategory) {
      query.subCategory = SUB_CATEGORY_TO_API[params.filters.subCategory];
    }
    if (params?.filters?.location) {
      query.internshipLocation =
        INTERN_LOCATION_TO_API[params.filters.location];
    }
    if (params?.filters?.departmentID) {
      query.departmentID = params.filters.departmentID;
    }
    if (params?.filters?.startDateFrom) {
      query.startDateFrom = params.filters.startDateFrom;
    }
    if (params?.filters?.startDateTo) {
      query.startDateTo = params.filters.startDateTo;
    }
    if (params?.filters?.school) {
      query.school = params.filters.school;
    }
    if (params?.filters?.schoolDepartment) {
      query.schoolDepartment = params.filters.schoolDepartment;
    }

    const { data: raw } = await apiClient.get<InternListResponseApi>(ENDPOINT, {
      params: query,
    });
    const list = Array.isArray(raw?.data)
      ? raw.data
      : Array.isArray(raw?.items)
        ? raw.items
        : [];
    const total = raw?.total ?? raw?.count ?? list.length;
    const items = list as InternListItemApi[];
    const data = items.map(mapInternFromApi);
    return { data, total, page, pageSize: limit };
  },

  /**
   * POST /api/admin/interns - Create intern (User + InternProfile).
   */
  create: async (body: CreateInternPayload): Promise<{ data: Intern }> => {
    const { data: raw } = await apiClient.post<{
      data?: InternListItemApi;
      success?: boolean;
    }>(ENDPOINT, body);
    const item = raw?.data;
    if (!item) throw new Error(i18n.t("internApi.createFailed"));
    return { data: mapInternFromApi(item) };
  },

  /**
   * GET /api/admin/interns/:id - Get intern profile by ID (full detail).
   */
  getById: async (id: string): Promise<{ data: InternDetail }> => {
    const { data: raw } = await apiClient.get<{
      data?: InternDetailApi;
      success?: boolean;
    }>(`${ENDPOINT}/${id}`);
    const item = raw?.data;
    if (!item) throw new Error(i18n.t("internApi.notFound"));
    return { data: mapInternDetailFromApi(item) };
  },

  /**
   * PATCH /api/admin/interns/:id - Update intern profile.
   * Backend may return 204 No Content (no body) – treat as success and refetch.
   */
  update: async (
    id: string,
    body: Partial<CreateInternPayload>,
  ): Promise<{ data: InternDetail }> => {
    await apiClient.patch<{ data?: InternDetailApi; success?: boolean }>(
      `${ENDPOINT}/${id}`,
      body,
    );
    const { data } = await internManagementApi.getById(id);
    return { data };
  },

  /**
   * DELETE /api/admin/interns/:id - Stajyer profil kaydını siler; hesabı (user) varsa soft-delete edilir.
   * Yetki: JWT + USERS:DELETE gerekli.
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`${ENDPOINT}/${id}`);
  },

  /**
   * GET /api/admin/interns/export - Liste filtreleri ile Excel dışa aktarma.
   * Response: binary stream; filename Content-Disposition header'ından alınır.
   */
  exportExcel: async (params?: {
    filters?: InternFilters;
    supervisorID?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<{ blob: Blob; filename: string }> => {
    const query: Record<string, string | number | undefined> = {};
    if (params?.filters?.search) query.search = params.filters.search;
    if (params?.supervisorID) query.supervisorID = params.supervisorID;
    if (params?.sortBy) query.sortBy = params.sortBy;
    if (params?.sortOrder) query.sortOrder = params.sortOrder;
    if (params?.filters?.status != null) query.status = params.filters.status;
    if (params?.filters?.applicationSource != null) {
      query.applicationSource =
        APPLICATION_SOURCE_TO_API[params.filters.applicationSource];
    }
    if (params?.filters?.internTypeV2) {
      query.internshipType =
        INTERNSHIP_TYPE_V2_TO_API[params.filters.internTypeV2];
    } else if (params?.filters?.internType != null) {
      query.internshipType = INTERN_TYPE_TO_API[params.filters.internType];
    }
    if (params?.filters?.subCategory) {
      query.subCategory = SUB_CATEGORY_TO_API[params.filters.subCategory];
    }
    if (params?.filters?.location) {
      query.internshipLocation =
        INTERN_LOCATION_TO_API[params.filters.location];
    }
    if (params?.filters?.departmentID) {
      query.departmentID = params.filters.departmentID;
    }
    if (params?.filters?.startDateFrom) {
      query.startDateFrom = params.filters.startDateFrom;
    }
    if (params?.filters?.startDateTo) {
      query.startDateTo = params.filters.startDateTo;
    }
    if (params?.filters?.school) query.school = params.filters.school;
    if (params?.filters?.schoolDepartment) {
      query.schoolDepartment = params.filters.schoolDepartment;
    }

    const res = await apiClient.get<Blob>(`${ENDPOINT}/export`, {
      params: query,
      responseType: "blob",
    });
    const header = res.headers["content-disposition"] as string | undefined;
    const today = new Date().toISOString().slice(0, 10);
    let filename = `stajyerler-${today}.xlsx`;
    if (header) {
      const utf8 = /filename\*=UTF-8''([^;\n]+)/i.exec(header);
      if (utf8) {
        filename = decodeURIComponent(utf8[1].trim());
      } else {
        const quoted = /filename="([^"]+)"/i.exec(header);
        if (quoted) filename = quoted[1];
        else {
          const loose = /filename=([^;\n]+)/i.exec(header);
          if (loose) filename = loose[1].replace(/"/g, "").trim();
        }
      }
    }
    return { blob: res.data as Blob, filename };
  },

  /**
   * GET /api/admin/interns/:id/certificate.pdf - Sertifika PDF'i indir.
   * hasCertificate=true olduğunda çağrılmalı; arka planda BullMQ ile üretilir.
   */
  downloadCertificate: async (
    id: string,
  ): Promise<{ blob: Blob; filename: string }> => {
    const res = await apiClient.get<Blob>(`${ENDPOINT}/${id}/certificate.pdf`, {
      responseType: "blob",
    });
    const header = res.headers["content-disposition"] as string | undefined;
    let filename = `sertifika-${id}.pdf`;
    if (header) {
      const utf8 = /filename\*=UTF-8''([^;\n]+)/i.exec(header);
      if (utf8) {
        filename = decodeURIComponent(utf8[1].trim());
      } else {
        const quoted = /filename="([^"]+)"/i.exec(header);
        if (quoted) filename = quoted[1];
        else {
          const loose = /filename=([^;\n]+)/i.exec(header);
          if (loose) filename = loose[1].replace(/"/g, "").trim();
        }
      }
    }
    return { blob: res.data as Blob, filename };
  },
};

/** Blob + filename'i tarayıcıda indirilen dosyaya dönüştür. */
export function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
